/*
 * Branded Best House: names on the metal card (blocks/bb-card-names.liquid).
 * The front of the card stays Nick's logo preview, untouched. This block adds one person's details for the BACK
 * (name, title, phone, email, website) as line-item properties next to Nick's own ("Card name", "Card title", …), and
 * draws a live "Back of the card" preview: the same card photo, with the text engraved in the tone Nick's tool uses
 * for the logo (polished in the card's metal, frosted on mirror, polished steel on black), read from
 * window.BBLogoPresets['metal-card']. Each person is one cart line at the pack picked; after a confirmed add, the
 * person fields clear for the next person.
 *
 * The first half is PURE (layout, fit, palette, checks) and is exported for node tests (tests/house/names.test.js).
 * The second half wires it to the page. No libraries; every part fails quietly. Nick's files are never touched.
 */
(function (root) {
  'use strict';

  var LIMITS = { name: 40, title: 60, phone: 30, email: 60, website: 60 };
  // Sizes in photo pixels on the 1000 x 1250 card photo (85 mm card = 781 px, about 9.2 px per mm): the name about
  // 5.6 mm, contact lines about 3 mm (8.5pt). The smallest legible engraved size is the supplier's to confirm.
  var SIZES = { name: 52, title: 32, contact: 28 };
  var GAP = { afterName: 0.42, afterTitle: 0.9, line: 0.5 }; // in multiples of the line's own size
  var MIN_SCALE = 0.85; // contact lines never below ~7pt in the preview: past that, "we'll confirm the fit"
  var MARGIN_MM = 7;

  // what is engraved, posted and matched is the same tidy text
  function tidy(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }

  // The example on an empty card back: greyed, drawing only.
  var EXAMPLE = { name: 'Your Name', title: 'Your Title' };
  var EXAMPLE_LINES = linesOf(EXAMPLE);

  // The lines to engrave, in order, empty optional lines skipped.
  function linesOf(f) {
    var out = [];
    var name = tidy(f.name), title = tidy(f.title);
    if (name) out.push({ role: 'name', text: name });
    if (title) out.push({ role: 'title', text: title });
    ['phone', 'email', 'website'].forEach(function (k) {
      var v = tidy(f[k]);
      if (v) out.push({ role: 'contact', text: v });
    });
    return out;
  }

  // Fit the lines in the face box. measure(text, sizePx, role) -> width in px. Returns positions in photo px.
  function layout(lines, face, align, measure) {
    var pxPerMM = (face.x1 - face.x0) / 85;
    var margin = MARGIN_MM * pxPerMM;
    var boxW = face.x1 - face.x0 - 2 * margin;
    var boxH = face.y1 - face.y0 - 2 * margin;
    function sizeOf(l, k) { return (l.role === 'name' ? SIZES.name : l.role === 'title' ? SIZES.title : SIZES.contact) * k; }
    function gapAfter(l, i, k) {
      if (i === lines.length - 1) return 0;
      var next = lines[i + 1];
      if (l.role === 'name') return sizeOf(l, k) * (next.role === 'title' ? GAP.afterName : GAP.afterTitle);
      if (l.role === 'title') return sizeOf(l, k) * GAP.afterTitle;
      return sizeOf(l, k) * GAP.line;
    }
    function measureAll(k) {
      var h = 0, w = 0;
      lines.forEach(function (l, i) {
        var s = sizeOf(l, k);
        w = Math.max(w, measure(l.text, s, l.role));
        h += s + gapAfter(l, i, k);
      });
      return { w: w, h: h };
    }
    var k = 1, m = measureAll(1);
    if (lines.length) {
      var need = Math.min(boxW / Math.max(m.w, 1), boxH / Math.max(m.h, 1), 1);
      k = Math.max(need, MIN_SCALE);
      m = measureAll(k);
    }
    var tight = lines.length > 0 && (m.w > boxW + 0.5 || m.h > boxH + 0.5);
    // 'center' and 'left' sit mid-card; 'corner' (Bottom left, Brady 2026-10-02) anchors the block in the lower-left corner
    var left = align === 'left' || align === 'corner';
    var x = left ? face.x0 + margin : (face.x0 + face.x1) / 2;
    var y = align === 'corner' ? face.y1 - margin - m.h : (face.y0 + face.y1) / 2 - m.h / 2;
    var placed = lines.map(function (l, i) {
      var s = sizeOf(l, k);
      var item = { role: l.role, text: l.text, size: s, x: x, y: y, align: left ? 'left' : 'center' };
      y += s + gapAfter(l, i, k);
      return item;
    });
    return { lines: placed, scale: k, tight: tight, box: { w: boxW, h: boxH } };
  }

  // Nick's rule for the card's tone (bb-logo-studio.js flatPaletteFor, copied, never edited): photos are named
  // metal-card-<finish>-<color>; black cards engrave as polished steel; mirror cards get a frosted mark.
  function paletteOf(src) {
    var name = String(src || '').toLowerCase();
    var m = name.match(/metal-card-([a-z-]+?)-(silver|gold|black|copper)\b/);
    var finish = m ? m[1] : 'brushed', color = m ? m[2] : 'silver';
    return { kind: finish === 'mirror' ? 'frosted' : 'polished', metal: color === 'black' ? 'silver' : color, color: color, finish: finish };
  }

  // Errors block the add (a name is required; lengths; an email that can't be one). Phone is only ever a hint:
  // business cards carry "M: 555 …", "Office 555 …", "555-0102 cell".
  function check(f) {
    var e = {};
    if (!tidy(f.name)) e.name = 'Add the name for this card.';
    Object.keys(LIMITS).forEach(function (k) {
      if (tidy(f[k]).length > LIMITS[k]) e[k] = 'Keep it under ' + LIMITS[k] + ' characters.';
    });
    var em = tidy(f.email);
    if (em && !e.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Check the email address.';
    return e;
  }
  function hints(f) {
    var h = {};
    var ph = tidy(f.phone);
    if (ph && (ph.replace(/\D/g, '').length < 7)) h.phone = 'Check the phone number.';
    return h;
  }

  // US numbers get their dashes as they're typed (Nick, 2026-10-06: "add the dashes in the number automatically
  // (763-218-5723)"): 7632185723 -> 763-218-5723, 17632185723 -> 1-763-218-5723, +17632185723 -> +1 763-218-5723.
  // Anything else stays exactly as typed: letters (an "ext. 12"), other countries (+44 ...), more than 11 digits.
  function formatPhone(s) {
    var raw = String(s == null ? '' : s);
    if (/[^\d\s().+\-]/.test(raw)) return raw;
    var t = raw.trim(), plus = t.charAt(0) === '+', d = t.replace(/\D/g, '');
    if (plus && d.charAt(0) !== '1') return raw;
    var cc = '';
    if (plus) { cc = '+1 '; d = d.slice(1); } else if (d.length === 11 && d.charAt(0) === '1') { cc = '1-'; d = d.slice(1); }
    if (d.length > 10) return raw;
    var out = d.slice(0, 3);
    if (d.length > 3) out += '-' + d.slice(3, 6);
    if (d.length > 6) out += '-' + d.slice(6);
    return cc + out;
  }

  var api = { LIMITS: LIMITS, SIZES: SIZES, MIN_SCALE: MIN_SCALE, tidy: tidy, linesOf: linesOf, layout: layout, paletteOf: paletteOf, check: check, hints: hints, formatPhone: formatPhone };
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; return; }
  root.BBCardNames = api;

  /* ------------------------------------------------------------------ page */
  var FALLBACK_FACE = { x0: 109, y0: 355, x1: 890, y1: 853 };
  var FONT = "'Archivo', 'Helvetica Neue', Arial, sans-serif";
  var EV_SELECT = 'shopify:product:select';      // Horizon: a pack / finish / colour is being changed
  var EV_LINES = 'shopify:cart:lines-update';    // Horizon: an add to cart, with a promise of its result
  var KEYS = ['name', 'title', 'phone', 'email', 'website'];

  function fontFor(role, size) { return (role === 'name' ? '600 ' : '500 ') + size + 'px ' + FONT; }
  function spacingFor(role) { return role === 'title' ? 0.04 : 0; } // em

  function initBlock(el) {
    if (el.dataset.ready) return;
    el.dataset.ready = '1';
    var section = el.closest('dialog') || el.closest('.shopify-section') || document;
    var form = document.getElementById(el.getAttribute('data-form'));
    var fields = {};
    KEYS.forEach(function (k) { fields[k] = el.querySelector('[data-cn="' + k + '"]'); });
    var layoutInputs = [].slice.call(el.querySelectorAll('[data-cn-layout]'));
    var modeInputs = [].slice.call(el.querySelectorAll('[data-cn-mode]'));
    var personFields = el.querySelector('[data-cn-fields]');
    var canvas = el.querySelector('[data-cn-canvas]');
    var fitNote = el.querySelector('[data-cn-fit]');
    var added = el.querySelector('[data-cn-added]');
    var countEl = el.querySelector('[data-cn-count]');
    // drawn only, never read back, so a photo from the CDN without CORS headers still works
    var photo = new Image(), photoSrc = '', photoReady = false;
    var layer = document.createElement('canvas');
    var busy = null;      // the promise of a pack / finish change in flight
    var pending = null;   // the person an add was sent for
    var attempted = false; // after an add attempt, every problem stays shown until it's fixed

    function values() {
      var v = {};
      KEYS.forEach(function (k) { v[k] = fields[k] ? fields[k].value : ''; });
      return v;
    }
    function align() {
      var on = layoutInputs.filter(function (i) { return i.checked; })[0];
      if (on && on.value === 'Left aligned') return 'left';
      return on && on.value === 'Bottom left' ? 'corner' : 'center';
    }
    function namesOn() {
      var on = modeInputs.filter(function (i) { return i.checked; })[0];
      return !on || on.value === 'names';
    }

    // an empty optional field posts nothing (no blank "Card phone:" on the order): its name is set only with a value
    function syncNames() {
      ['title', 'phone', 'email', 'website'].forEach(function (k) {
        var f = fields[k]; if (!f) return;
        if (!f.dataset.prop) f.dataset.prop = f.getAttribute('name') || '';
        if (tidy(f.value)) f.setAttribute('name', f.dataset.prop); else f.removeAttribute('name');
      });
    }

    // Logo only: every input here is disabled, so the line is posted exactly as it is today.
    function applyMode() {
      var on = namesOn();
      [].slice.call(el.querySelectorAll('[data-cn-prop]')).forEach(function (i) { i.disabled = !on; });
      if (personFields) personFields.hidden = !on;
      if (fields.name) fields.name.required = on;
      schedule();
    }

    // the card photo the studio shows (Nick's search: visible, the preset's shape), else any card photo in the gallery
    function cardImg() {
      var P = root.BBLogoPresets && root.BBLogoPresets['metal-card'];
      var ratio = P ? P.photoW / P.photoH : 0.8;
      var imgs = [].slice.call(section.querySelectorAll('.product-media img, media-gallery img, slideshow-slide img'));
      var isCard = function (i) { return /metal-card-/i.test(i.currentSrc || i.src || ''); };
      var fits = function (i) { return !i.naturalWidth || Math.abs(i.naturalWidth / i.naturalHeight - ratio) < 0.02; };
      var vis = imgs.filter(function (i) { return isCard(i) && fits(i) && i.getClientRects().length > 0 && i.getBoundingClientRect().width > 0; });
      return vis[0] || imgs.filter(isCard)[0] || null;
    }
    // a 1000px-wide copy from Shopify's CDN: sharp enough for the crop, the frame the preset was measured on
    function bestSrc(img) {
      var src = img.currentSrc || img.src || '';
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(src)) return src;
      if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=1000');
      return src + (src.indexOf('?') < 0 ? '?' : '&') + 'width=1000';
    }
    function syncPhoto() {
      var img = cardImg();
      var src = img ? bestSrc(img) : '';
      if (!src || src === photoSrc) return;
      photoSrc = src; photoReady = false;
      photo.onload = function () { photoReady = true; schedule(); };
      photo.onerror = function () { photoReady = false; schedule(); };
      photo.src = src;
    }

    var raf = 0;
    function schedule() { if (!raf) raf = requestAnimationFrame(function () { raf = 0; draw(); }); }

    function draw() {
      if (!canvas) return;
      var P = (root.BBLogoPresets && root.BBLogoPresets['metal-card']) || null;
      var face = (P && P.face) || FALLBACK_FACE;
      // crop: the card plus a little of the studio backdrop around it
      var cx0 = face.x0 - 50, cy0 = face.y0 - 50, cw = face.x1 - face.x0 + 100, ch = face.y1 - face.y0 + 100;
      canvas.style.aspectRatio = cw + ' / ' + ch;
      var dpr = Math.min(root.devicePixelRatio || 1, 2);
      var cssW = canvas.clientWidth || 360;
      var scale = cssW / cw;
      var W = Math.round(cw * scale * dpr), H = Math.round(ch * scale * dpr);
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
      var ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, W, H);
      ctx.setTransform(scale * dpr, 0, 0, scale * dpr, -cx0 * scale * dpr, -cy0 * scale * dpr);
      if (photoReady) {
        ctx.drawImage(photo, 0, 0, P ? P.photoW : 1000, P ? P.photoH : 1250);
      } else {
        ctx.fillStyle = '#d9d9d6';
        ctx.fillRect(face.x0, face.y0, face.x1 - face.x0, face.y1 - face.y0);
      }
      var lines = namesOn() ? linesOf(values()) : [];
      // nothing typed yet: a greyed example shows where the details go (never posted, never on the order)
      var example = namesOn() && !lines.length;
      if (example) lines = EXAMPLE_LINES;
      var lay = layout(lines, face, align(), function (text, size, role) {
        ctx.font = fontFor(role, size);
        return ctx.measureText(text).width + spacingFor(role) * size * Math.max(text.length - 1, 0);
      });
      if (fitNote) fitNote.hidden = example || !lay.tight;
      el.setAttribute('data-cn-lines', String(example ? 0 : lines.length));
      el.toggleAttribute('data-cn-example', example);
      if (!lines.length) return;
      // the text on its own layer, then coloured the way Nick's tool colours an engraved logo on this card
      if (layer.width !== W || layer.height !== H) { layer.width = W; layer.height = H; }
      var lg = layer.getContext('2d');
      lg.setTransform(1, 0, 0, 1, 0, 0);
      lg.globalCompositeOperation = 'source-over';
      lg.clearRect(0, 0, W, H);
      lg.setTransform(scale * dpr, 0, 0, scale * dpr, -cx0 * scale * dpr, -cy0 * scale * dpr);
      lg.textBaseline = 'top';
      lay.lines.forEach(function (l) {
        lg.font = fontFor(l.role, l.size);
        lg.textAlign = l.align;
        if ('letterSpacing' in lg) lg.letterSpacing = spacingFor(l.role) + 'em';
        lg.fillText(l.text, l.x, l.y);
      });
      lg.globalCompositeOperation = 'source-atop';
      var pal = paletteOf(photoSrc);
      if (P && pal.kind === 'polished' && P.metals && P.metals[pal.metal]) {
        var g = lg.createLinearGradient(face.x0, face.y0, face.x1, face.y1);
        P.metals[pal.metal].forEach(function (s) { g.addColorStop(s[0], s[1]); });
        lg.fillStyle = g;
      } else {
        lg.fillStyle = (P && P.frosted && P.frosted[pal.metal]) || '#b3b2b2';
      }
      lg.fillRect(face.x0, face.y0, face.x1 - face.x0, face.y1 - face.y0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.save();
      ctx.shadowColor = pal.kind === 'polished' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.3)';
      ctx.shadowOffsetY = Math.max(0.5, scale * dpr * 1.2);
      ctx.shadowBlur = Math.max(0.5, scale * dpr * 1.4);
      if (example) ctx.globalAlpha = 0.4;
      ctx.drawImage(layer, 0, 0);
      ctx.restore();
    }

    // Errors show on leaving a field and on an add attempt, never mid-word; they clear as soon as the value is fine.
    function showErrors(all) {
      var on = namesOn();
      var e = on ? check(values()) : {}, h = on ? hints(values()) : {};
      KEYS.forEach(function (k) {
        var f = fields[k]; if (!f) return;
        var msg = el.querySelector('[data-cn-err="' + k + '"]');
        var shown = all || attempted || f.dataset.touched;
        var err = e[k] && shown ? e[k] : '';
        if (k === 'name' && !tidy(f.value) && !all && !attempted) err = ''; // an empty name is only flagged once they try to add
        var text = err || (shown && h[k]) || '';
        f.setCustomValidity(err);
        f.setAttribute('aria-invalid', err ? 'true' : 'false');
        if (msg) { msg.textContent = text; msg.hidden = !text; }
      });
    }

    // the phone's dashes go in as it's typed; the caret stays after the same digit, and a backspace on a dash takes
    // the digit before it too (otherwise the dash would come straight back)
    function dashPhone(f, ev) {
      var v = f.value, prev = f.dataset.prev || '';
      var pos = f.selectionStart == null ? v.length : f.selectionStart;
      var before = v.slice(0, pos).replace(/\D/g, '').length;
      if (ev && ev.inputType === 'deleteContentBackward' && prev.length === v.length + 1 && /\D/.test(prev.charAt(pos)) && before > 0) {
        v = v.slice(0, pos).replace(/\d(\D*)$/, '$1') + v.slice(pos);
        before -= 1;
      }
      var nv = formatPhone(v);
      if (nv !== f.value) {
        f.value = nv;
        var p = 0, seen = 0;
        while (seen < before && p < nv.length) { if (/\d/.test(nv.charAt(p))) seen += 1; p += 1; }
        if (before === 0) p = Math.min(pos, nv.length);
        try { f.setSelectionRange(p, p); } catch (e) { /* not focused */ }
      }
      f.dataset.prev = f.value;
    }

    KEYS.forEach(function (k, i) {
      var f = fields[k]; if (!f) return;
      f.addEventListener('input', function (ev) {
        if (k === 'phone') dashPhone(f, ev);
        syncNames(); schedule(); if (f.getAttribute('aria-invalid') === 'true') showErrors(false);
      });
      if (k === 'phone') f.addEventListener('focus', function () { f.dataset.prev = f.value; });
      f.addEventListener('change', function () { f.dataset.touched = '1'; showErrors(false); });
      f.addEventListener('blur', function () { if (tidy(f.value)) { f.dataset.touched = '1'; showErrors(false); } });
      // Enter (the phone's Go key) moves to the next field instead of adding this person half-typed
      f.addEventListener('keydown', function (ev) {
        if (ev.key !== 'Enter' || ev.isComposing) return;
        ev.preventDefault();
        var next = KEYS.slice(i + 1).map(function (n) { return fields[n]; }).filter(Boolean)[0];
        if (next) next.focus(); else f.blur();
      });
    });
    layoutInputs.forEach(function (i) { i.addEventListener('change', schedule); });
    modeInputs.forEach(function (i) { i.addEventListener('change', applyMode); });
    el.addEventListener('bb-cn-redraw', schedule);

    // finish, colour or pack changed: Horizon swaps the gallery (replaceWith) or its image src; redraw on the new card
    new MutationObserver(function () { syncPhoto(); }).observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    if (document.fonts && document.fonts.load) document.fonts.load('600 40px Archivo').then(schedule, function () {});
    if ('ResizeObserver' in root && canvas) new ResizeObserver(schedule).observe(canvas);

    // A pack / finish change in flight: Horizon would queue an add without any properties (no name, no logo), so an
    // add clicked meanwhile waits for the change to finish, then clicks again.
    document.addEventListener(EV_SELECT, function (ev) {
      if (!section.contains(ev.target)) return;
      var p = ev.promise || (ev.detail && ev.detail.promise);
      if (!p || typeof p.then !== 'function') return;
      var mine = busy = p.then(function () {}, function () {}).then(function () {
        return new Promise(function (r) { setTimeout(r, 60); }); // let the form finish its own update
      }).then(function () { if (busy === mine) busy = null; });
    }, true);

    el.bbCardNamesBusy = function () { return !!busy; };

    function isAdd(btn) {
      if (!btn || !form) return false;
      if (btn.classList && btn.classList.contains('sticky-add-to-cart__button')) return true;
      return btn.type === 'submit' && btn.form === form;
    }

    // Every add click passes here first (window, capture: before Horizon's own document-level handlers).
    root.addEventListener('click', function (ev) {
      var btn = ev.target && ev.target.closest && ev.target.closest('button');
      if (!isAdd(btn) || !namesOn() || el.closest('[hidden]')) return;
      // what is previewed is what is posted and what we match: tidy the text in place before the form reads it
      KEYS.forEach(function (k) { var f = fields[k]; if (f && f.value !== tidy(f.value)) f.value = tidy(f.value); });
      syncNames();
      if (busy) {
        ev.preventDefault(); ev.stopImmediatePropagation();
        var again = btn;
        busy.then(function () { setTimeout(function () { again.click(); }, 0); });
        return;
      }
      attempted = true;
      showErrors(true);
      if (!form.checkValidity()) {
        ev.preventDefault(); ev.stopImmediatePropagation();
        var bad = KEYS.map(function (k) { return fields[k]; }).filter(function (f) { return f && !f.checkValidity(); })[0];
        if (bad) { bad.scrollIntoView({ block: 'center', behavior: 'smooth' }); bad.focus({ preventScroll: true }); }
        else form.reportValidity();
        return;
      }
      var v = values();
      pending = { name: tidy(v.name), title: tidy(v.title) };
    }, true);

    // One outcome per add, from Horizon's own result: a failed add keeps what was typed.
    document.addEventListener(EV_LINES, function (ev) {
      if (!pending || !form) return;
      var pfc = form.closest('product-form-component');
      if (pfc && ev.target !== pfc && !(pfc.contains && pfc.contains(ev.target))) return;
      var mine = pending; pending = null;
      var p = ev.promise || (ev.detail && ev.detail.promise);
      if (!p || typeof p.then !== 'function') return;
      p.then(function (r) {
        var d = r && r.detail;
        if (d && !d.didError) done(mine, d.items || []);
      }, function () {});
    }, true);

    function sameCard(it, who) {
      var pr = it.properties || {};
      return pr['Card name'] === who.name && (pr['Card title'] || '') === who.title;
    }
    function done(who, items) {
      var line = items.filter(function (it) { return sameCard(it, who); })[0];
      var pack = 0;
      if (line) {
        var pk = (line.options_with_values || []).filter(function (o) { return o.name === 'Pack'; })[0];
        pack = parseInt(String(pk ? pk.value : '').replace(/,/g, ''), 10) || 0;
      }
      var cards = line && pack ? line.quantity * pack : 0;
      var productId = el.getAttribute('data-product-id');
      var people = items.filter(function (it) { return it.properties && it.properties['Card name'] && (!productId || String(it.product_id) === productId); }).length;
      attempted = false;
      ['name', 'title', 'phone', 'email'].forEach(function (k) {   // the website is usually shared: it stays
        var f = fields[k]; if (!f) return;
        f.value = ''; f.setCustomValidity(''); f.removeAttribute('aria-invalid'); delete f.dataset.touched;
      });
      syncNames();
      showErrors(false);
      schedule();
      if (added) {
        added.textContent = '';
        setTimeout(function () {
          added.textContent = 'Added ' + who.name + (cards ? ': ' + cards.toLocaleString('en-US') + ' cards' : '') + '. Add the next person, or check out.';
        }, 120);
      }
      if (countEl) countEl.textContent = people ? people + (people === 1 ? ' person' : ' people') + ' in your cart.' : '';
    }

    syncNames();
    applyMode();
    syncPhoto();
    schedule();
  }

  function all() { [].slice.call(document.querySelectorAll('[data-bb-card-names]')).forEach(initBlock); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
  document.addEventListener('shopify:section:load', all);
  // Nick's presets arrive with bb-logo-studio.js; redraw once they're there
  root.addEventListener('bb-logo-studio:ready', function () {
    [].slice.call(document.querySelectorAll('[data-bb-card-names]')).forEach(function (b) { b.dispatchEvent(new Event('bb-cn-redraw')); });
  });
})(typeof window !== 'undefined' ? window : this);
